/**
 * @file Colors constants for the shared workspace. Colors are sourced from CSS custom properties defined in tailwind.css
 * The values are used for TailwindCSS and MUI integration.
 */

/**
 * Helper function to get CSS custom property value
 */

const Cache = new Map<string, string>()
function getCSSVariable(propertyName: string): string {
  if (typeof document === 'undefined') return '#000001'
  if (Cache.has(propertyName)) return Cache.get(propertyName) || '#000002'
  const styles = getComputedStyle(document.documentElement)
  const value = styles.getPropertyValue(propertyName).trim()
  if (value) Cache.set(propertyName, value)
  return value || '#000003'
}

export const Colors = {
  // Brand
  white: '#ffffff',

  // Gray
  get primaryBlack() {
    return getCSSVariable('--color-primary-black')
  },
  get darkGray() {
    return getCSSVariable('--color-dark-gray')
  },
  get midGray() {
    return getCSSVariable('--color-mid-gray')
  },
  get lightGray1() {
    return getCSSVariable('--color-light-gray1')
  },
  get lightGray2() {
    return getCSSVariable('--color-light-gray2')
  },
  get lightGray3() {
    return getCSSVariable('--color-light-gray3')
  },
  get lightGray4() {
    return getCSSVariable('--color-light-gray4')
  },
  get bgGray() {
    return getCSSVariable('--color-bg-gray')
  },

  // Secondary
  get royalBlue() {
    return getCSSVariable('--color-royal-blue')
  },
  get darkRoyalBlue() {
    return getCSSVariable('--color-dark-royal-blue')
  },
  get cyanL1() {
    return getCSSVariable('--color-cyan-l1')
  },
  get cyan() {
    return getCSSVariable('--color-cyan')
  },
  get cyanD1() {
    return getCSSVariable('--color-cyan-d1')
  },
  get limeGreenL1() {
    return getCSSVariable('--color-lime-green-l1')
  },
  get limeGreen() {
    return getCSSVariable('--color-lime-green')
  },
  get kellyGreen() {
    return getCSSVariable('--color-kelly-green')
  },
  get kellyGreenD1() {
    return getCSSVariable('--color-kelly-green-d1')
  },
  get mangoYellow() {
    return getCSSVariable('--color-mango-yellow')
  },
  get marigoldOrange() {
    return getCSSVariable('--color-marigold-orange')
  },
  get violetL1() {
    return getCSSVariable('--color-violet-l1')
  },
  get violet() {
    return getCSSVariable('--color-violet')
  },
  get violetD1() {
    return getCSSVariable('--color-violet-d1')
  },

  // Utility
  get success() {
    return getCSSVariable('--color-success')
  },
  get successD1() {
    return getCSSVariable('--color-success-d1')
  },
  get warning() {
    return getCSSVariable('--color-warning')
  },
  get warningD1() {
    return getCSSVariable('--color-warning-d1')
  },
  get errorL1() {
    return getCSSVariable('--color-error-l1')
  },
  get error() {
    return getCSSVariable('--color-error')
  },
  get errorD1() {
    return getCSSVariable('--color-error-d1')
  },
  get informationL1() {
    return getCSSVariable('--color-information-l1')
  },
  get information() {
    return getCSSVariable('--color-information')
  },
  get informationD1() {
    return getCSSVariable('--color-information-d1')
  },
  get blueGray() {
    return getCSSVariable('--color-blue-gray')
  },
  get sidebarGray() {
    return getCSSVariable('--color-sidebar-gray')
  },
  get magenta() {
    return getCSSVariable('--color-magenta')
  },

  // Form
  get formFill() {
    return getCSSVariable('--color-form-fill')
  },
  get formStroke() {
    return getCSSVariable('--color-form-stroke')
  },
} as const
